import { zodResolver } from "@hookform/resolvers/zod";
import { ApiError, fieldErrorsOf } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import type { Resolver } from "react-hook-form";
import { Link } from "react-router";
import { scheduleApi, scheduleKeys, type ScheduleResponse } from "../schedule/scheduleApi";
import { createAuthedAddressSearch } from "../venue-location/addressApi";
import {
  VenueLocationSection,
  type VenueLocationValue,
} from "../venue-location/VenueLocationSection";
import {
  onboardingApi,
  onboardingKeys,
  type ListingDecision,
  type OnboardingProgressResponse,
} from "./onboardingApi";
import {
  capacityFormSchema,
  profileFormSchema,
  type CapacityFormValues,
  type ProfileFormValues,
} from "./onboardingRules";
import {
  applyFirstOpenDay,
  needsDefaultService,
  toQuickWeek,
  toWindows,
  validateQuickDays,
  type QuickDay,
  type QuickHoursIssue,
  type QuickRange,
} from "./quickHours";

const inputStyle = css({
  border: "1px solid",
  borderColor: "border.default",
  borderRadius: "md",
  px: "3",
  py: "2",
  fontSize: "sm",
  w: "full",
});

const primaryBtn = css({
  bg: "colorPalette.default",
  color: "colorPalette.fg",
  px: "4",
  py: "2",
  borderRadius: "md",
  fontWeight: "semibold",
  fontSize: "sm",
  cursor: "pointer",
  _disabled: { opacity: 0.5, cursor: "not-allowed" },
});

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className={css({ display: "flex", flexDirection: "column", gap: "1" })}>
      <span className={css({ fontSize: "sm", fontWeight: "medium" })}>{label}</span>
      {children}
      {error && (
        <span role="alert" className={css({ fontSize: "xs", color: "red.default" })}>
          {error}
        </span>
      )}
    </label>
  );
}

/** A green "✓ Saved" confirmation shown beside a step's save button (mirrors the ReadyPanel note). */
function SavedNote({ show, label }: { show: boolean; label: string }) {
  if (!show) return null;
  return (
    <output
      className={css({
        fontSize: "sm",
        fontWeight: "medium",
        color: "green.text",
        display: "inline-flex",
        alignItems: "center",
        gap: "1",
      })}
    >
      <span aria-hidden>✓</span>
      {label}
    </output>
  );
}

/** PROFILE step — the venue identity fields (ADR-0027: zod → react-hook-form → controlled inputs). */
export function ProfileStep({
  venueId,
  version,
  onSaved,
}: {
  venueId: string;
  version: number;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  // Rehydrate the form from the saved values on load/reload (the wizard's progress read carries only
  // step state, not the values) — react-hook-form's `values` resets untouched fields when the data
  // arrives, and `keepDirtyValues` protects an in-progress edit from a background refetch.
  const profile = useQuery({
    queryKey: onboardingKeys.profile(venueId),
    queryFn: () => onboardingApi.getProfile(venueId),
  });
  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema(t)) as Resolver<ProfileFormValues>,
    defaultValues: { displayName: "", category: "RESTAURANT", contactPhone: "" },
    values: profile.data
      ? {
          displayName: profile.data.displayName,
          category: profile.data.category as ProfileFormValues["category"],
          contactPhone: profile.data.contactPhone ?? "",
        }
      : undefined,
    resetOptions: { keepDirtyValues: true },
    mode: "onSubmit",
  });

  const submit = form.handleSubmit(async (data) => {
    try {
      await onboardingApi.updateProfile(venueId, {
        displayName: data.displayName,
        // Onboarding no longer collects the raison sociale; preserve any value set elsewhere (billing)
        // so this profile PATCH never clears it.
        legalName: profile.data?.legalName ?? null,
        category: data.category,
        contactPhone: data.contactPhone || null,
        contactCountry: "FR",
        expectedVersion: profile.data?.version ?? version,
      });
      setSaved(true);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError("displayName", { type: "server", message: t("onboarding.error.stale") });
        return;
      }
      for (const [field, message] of Object.entries(fieldErrorsOf(err))) {
        const mapped = (["displayName", "category", "contactPhone"] as const).includes(
          field as never,
        )
          ? (field as keyof ProfileFormValues)
          : "displayName";
        form.setError(mapped, { type: "server", message });
      }
    }
  });

  const err = (name: keyof ProfileFormValues) =>
    form.formState.errors[name]?.message as string | undefined;

  return (
    <form
      onSubmit={(e) => void submit(e)}
      onChange={() => setSaved(false)}
      className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "md" })}
    >
      <Field label={t("onboarding.profile.displayName")} error={err("displayName")}>
        <input {...form.register("displayName")} className={inputStyle} />
      </Field>
      <Field label={t("onboarding.profile.category")} error={err("category")}>
        <select {...form.register("category")} className={inputStyle}>
          {[
            "RESTAURANT",
            "BAR",
            "WINE_BAR",
            "BRASSERIE",
            "CAFE",
            "PUB",
            "COCKTAIL_BAR",
            "NIGHTCLUB",
            "OTHER",
          ].map((c) => (
            <option key={c} value={c}>
              {t(`onboarding.category.${c}`)}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("onboarding.profile.phone")} error={err("contactPhone")}>
        <input
          {...form.register("contactPhone")}
          className={inputStyle}
          placeholder="06 12 34 56 78"
        />
      </Field>
      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        <button type="submit" disabled={form.formState.isSubmitting} className={primaryBtn}>
          {t("onboarding.save")}
        </button>
        <SavedNote show={saved} label={t("onboarding.saved")} />
      </div>
    </form>
  );
}

/** CAPACITY quick-start step — a single total-covers number (§C.5 CAPACITY_ONLY). */
export function CapacityStep({ venueId, onSaved }: { venueId: string; onSaved: () => void }) {
  const { t } = useTranslation();
  const [saved, setSaved] = useState(false);
  const capacity = useQuery({
    queryKey: onboardingKeys.capacity(venueId),
    queryFn: () => onboardingApi.getCapacity(venueId),
  });
  const form = useForm<CapacityFormValues>({
    resolver: zodResolver(capacityFormSchema(t)) as Resolver<CapacityFormValues>,
    defaultValues: { totalCovers: 40 },
    // Bind the saved covers into the input on load (was only shown as a read-only hint before).
    values:
      typeof capacity.data?.totalCovers === "number"
        ? { totalCovers: capacity.data.totalCovers }
        : undefined,
    resetOptions: { keepDirtyValues: true },
    mode: "onSubmit",
  });

  const submit = form.handleSubmit(async (data) => {
    try {
      await onboardingApi.putCapacity(venueId, data.totalCovers, capacity.data?.version ?? 0);
      setSaved(true);
      onSaved();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        form.setError("totalCovers", { type: "server", message: t("onboarding.error.stale") });
        return;
      }
      for (const [, message] of Object.entries(fieldErrorsOf(err))) {
        form.setError("totalCovers", { type: "server", message });
      }
    }
  });

  return (
    <form
      onSubmit={(e) => void submit(e)}
      onChange={() => setSaved(false)}
      className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "sm" })}
    >
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("onboarding.capacity.hint")}</p>
      <Field
        label={t("onboarding.capacity.totalCovers")}
        error={form.formState.errors.totalCovers?.message as string | undefined}
      >
        <input type="number" min={0} {...form.register("totalCovers")} className={inputStyle} />
      </Field>
      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        <button type="submit" disabled={form.formState.isSubmitting} className={primaryBtn}>
          {t("onboarding.save")}
        </button>
        <SavedNote show={saved} label={t("onboarding.saved")} />
      </div>
    </form>
  );
}

/**
 * ADDRESS step — an INLINE address form (BAN autocomplete + structured fields + map pin) instead of a
 * link-out to the Google place-match editor. Saving the location marks the ADDRESS step COMPLETE
 * server-side; no Google match or raison sociale is required. The Google listing match stays available
 * later in Settings → Channels.
 */
export function AddressStep({
  venueId,
  venueVersion,
  onSaved,
}: {
  venueId: string;
  venueVersion: number;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const search = useMemo(() => createAuthedAddressSearch(), []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = useQuery({
    queryKey: onboardingKeys.location(venueId),
    queryFn: () => onboardingApi.getLocation(venueId),
  });

  const saved = location.data;
  const initial: Partial<VenueLocationValue> | undefined = saved?.streetLine1
    ? {
        streetLine1: saved.streetLine1 ?? "",
        streetLine2: saved.streetLine2 ?? "",
        postalCode: saved.postalCode ?? "",
        locality: saved.locality ?? "",
        region: saved.region ?? "",
        country: saved.country ?? "",
        lat: saved.lat,
        lng: saved.lng,
        formattedAddress: saved.formattedAddress ?? "",
      }
    : undefined;

  const confirm = async (value: VenueLocationValue) => {
    setSaving(true);
    setError(null);
    // The location CAS is on the SHARED venue aggregate version, which earlier onboarding steps already
    // bumped — and GET /location gives no version until a location exists. So seed from the known venue
    // version, and if the server still reports STALE_WRITE it hands back `currentVersion`: rebase and
    // retry once. (An out-of-country pin comes back CONFIRMATION_REQUIRED — resubmit confirming it.)
    try {
      await saveLocation(value, saved?.version ?? venueVersion);
      await qc.invalidateQueries({ queryKey: onboardingKeys.location(venueId) });
      onSaved();
    } catch {
      setError(t("onboarding.error.generic"));
    } finally {
      setSaving(false);
    }
  };

  async function saveLocation(value: VenueLocationValue, expectedVersion: number): Promise<void> {
    try {
      await onboardingApi.confirmLocation(venueId, { ...value, expectedVersion });
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 409) throw err;
      const problem = err.problem as { errorCode?: string; currentVersion?: number } | undefined;
      if (problem?.errorCode === "STALE_WRITE" && typeof problem.currentVersion === "number") {
        await onboardingApi.confirmLocation(venueId, {
          ...value,
          expectedVersion: problem.currentVersion,
        });
        return;
      }
      if (problem?.errorCode === "CONFIRMATION_REQUIRED") {
        await onboardingApi.confirmLocation(venueId, {
          ...value,
          expectedVersion,
          confirmOutOfCountry: true,
        });
        return;
      }
      throw err;
    }
  }

  // Wait for the saved location before mounting the section (like HoursStep): VenueLocationSection seeds
  // its fields from `initial` in one-shot useState initializers, so mounting it before the GET resolves
  // would leave the saved address blank after a reload even though it persisted.
  if (location.isLoading) return <p>{t("onboarding.loading")}</p>;

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
      {error && (
        <p role="alert" className={css({ fontSize: "sm", color: "red.default" })}>
          {error}
        </p>
      )}
      <VenueLocationSection
        search={search}
        initial={initial}
        saving={saving}
        compact
        onConfirm={(value) => void confirm(value)}
      />
    </div>
  );
}

const HOUR_DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
type RangeSlot = "lunch" | "dinner";

const secondaryBtn = css({
  px: "3",
  py: "1.5",
  borderRadius: "md",
  border: "1px solid",
  borderColor: "border.default",
  fontSize: "sm",
  cursor: "pointer",
  w: "fit-content",
});

/**
 * HOURS step — a SIMPLE inline opening-hours editor so the manager sets a normal week without leaving the
 * wizard (the full services + weekly-grid editor stays in Settings → Horaires). A lunch and a dinner range
 * per day, with closed days; on save it ensures a bookable service exists only if a new range needs one,
 * then writes the week. Saving the schedule emits `ScheduleChanged`, which marks the HOURS step complete
 * server-side. The PUT replaces the whole week, so a week this form can't represent is read-only here.
 */
export function HoursStep({ venueId, onSaved }: { venueId: string; onSaved: () => void }) {
  const { t } = useTranslation();
  // Held here, not in the form: the form re-seeds (remounts) on every new schedule version, which must
  // not wipe the "saved" note or the stale-write message that caused the reload.
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const services = useQuery({
    queryKey: scheduleKeys.services(venueId),
    queryFn: () => scheduleApi.listServices(venueId),
  });
  const schedule = useQuery({
    queryKey: scheduleKeys.schedule(venueId),
    queryFn: () => scheduleApi.getSchedule(venueId),
  });
  if (services.isLoading || schedule.isLoading) return <p>{t("onboarding.loading")}</p>;
  // Never offer a save on top of a week we failed to read: the PUT would overwrite it.
  if (!services.data || !schedule.data) {
    return (
      <p role="alert" className={css({ fontSize: "sm", color: "red.default" })}>
        {t("onboarding.error.generic")}
      </p>
    );
  }
  const week = toQuickWeek(schedule.data.windows);
  if (week.kind === "advanced") return <AdvancedHours schedule={schedule.data} />;
  return (
    <HoursForm
      key={schedule.data.version}
      venueId={venueId}
      onSaved={onSaved}
      defaultServiceId={services.data.find((s) => s.onlineBookable)?.id ?? null}
      version={schedule.data.version}
      initialDays={week.days}
      saved={saved}
      setSaved={setSaved}
      error={error}
      setError={setError}
    />
  );
}

/** A week the quick form would have to change to show: listed read-only, edited in the full editor. */
function AdvancedHours({ schedule }: { schedule: ScheduleResponse }) {
  const { t } = useTranslation();
  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "xl" })}>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>
        {t("onboarding.hours.advancedWeek")}
      </p>
      <ul className={css({ display: "flex", flexDirection: "column", gap: "1", fontSize: "sm" })}>
        {HOUR_DAYS.map((day, i) => {
          const ranges = schedule.windows
            .filter((w) => w.dayOfWeek === i + 1)
            .toSorted((a, b) => a.startTime.localeCompare(b.startTime))
            .map((w) => `${w.startTime.slice(0, 5)}–${w.endTime.slice(0, 5)}`);
          return (
            <li key={day}>
              <strong>{t(`schedule.day.${day}`)}</strong> :{" "}
              {ranges.length > 0 ? ranges.join(", ") : t("onboarding.hours.closed")}
            </li>
          );
        })}
      </ul>
      <Link to="../schedule" className={primaryBtn} style={{ width: "fit-content" }}>
        {t("onboarding.hours.openEditor")}
      </Link>
    </div>
  );
}

function HoursForm({
  venueId,
  onSaved,
  defaultServiceId,
  version,
  initialDays,
  saved,
  setSaved,
  error,
  setError,
}: {
  venueId: string;
  onSaved: () => void;
  defaultServiceId: string | null;
  version: number;
  initialDays: QuickDay[];
  saved: boolean;
  setSaved: (saved: boolean) => void;
  error: string | null;
  setError: (error: string | null) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState<QuickDay[]>(initialDays);
  const [issues, setIssues] = useState<Map<number, QuickHoursIssue>>(new Map());

  const edit = (next: QuickDay[]) => {
    setDays(next);
    setSaved(false);
    setError(null);
    setIssues(new Map());
  };
  const setDay = (i: number, patch: Partial<QuickDay>) =>
    edit(days.map((row, j) => (j === i ? { ...row, ...patch } : row)));
  const setRange = (i: number, slot: RangeSlot, patch: Partial<QuickRange>) => {
    const row = days[i]!;
    setDay(i, { [slot]: { ...row[slot], ...patch } });
  };

  const save = async () => {
    const problems = validateQuickDays(days);
    setIssues(problems);
    if (problems.size > 0) return;
    setSaving(true);
    setError(null);
    let ok = false;
    try {
      // A new range must reference a service — create a default bookable one only if none exists.
      let serviceId = defaultServiceId;
      if (needsDefaultService(days) && !serviceId) {
        const created = await scheduleApi.createService(venueId, {
          nameFr: t("onboarding.hours.defaultServiceName"),
          partyMin: 1,
          partyMax: 10,
          onlineBookable: true,
          turnoverBufferMinutes: 0,
        });
        serviceId = created.id;
      }
      // Save against the version this form was seeded from, so a co-manager's edit comes back as a 409
      // instead of being silently overwritten. Creating a service doesn't move the schedule version.
      await scheduleApi.saveSchedule(venueId, version, toWindows(days, serviceId ?? ""));
      setSaved(true);
      ok = true;
    } catch (err) {
      setSaved(false);
      setError(
        err instanceof ApiError && err.status === 409
          ? t("onboarding.error.stale")
          : t("onboarding.error.generic"),
      );
    }
    // On failure too: a 409 reloads the form onto the current week, and a service may have been created.
    // The key family covers the schedule, so the go-live check sees these hours before onSaved moves on.
    await qc.invalidateQueries({ queryKey: scheduleKeys.services(venueId) });
    setSaving(false);
    if (ok) onSaved();
  };

  const rangeRow = (i: number, slot: RangeSlot) => {
    const dayLabel = t(`schedule.day.${HOUR_DAYS[i]!}`);
    const slotLabel = t(`onboarding.hours.${slot}`);
    const range = days[i]![slot];
    return (
      <div className={css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "2" })}>
        <label
          className={css({
            display: "flex",
            alignItems: "center",
            gap: "2",
            w: "5rem",
            fontSize: "sm",
          })}
        >
          <input
            type="checkbox"
            aria-label={`${dayLabel} ${slotLabel}`}
            checked={range.enabled}
            onChange={(e) => setRange(i, slot, { enabled: e.target.checked })}
          />
          <span aria-hidden>{slotLabel}</span>
        </label>
        {range.enabled && (
          <div className={css({ display: "flex", alignItems: "center", gap: "2" })}>
            <input
              type="time"
              aria-label={`${dayLabel} ${slotLabel} ${t("onboarding.hours.from")}`}
              value={range.start}
              onChange={(e) => setRange(i, slot, { start: e.target.value })}
              className={inputStyle}
            />
            <span className={css({ color: "fg.muted" })}>–</span>
            <input
              type="time"
              aria-label={`${dayLabel} ${slotLabel} ${t("onboarding.hours.to")}`}
              value={range.end}
              onChange={(e) => setRange(i, slot, { end: e.target.value })}
              className={inputStyle}
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "xl" })}>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t("onboarding.hours.intro")}</p>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>
        {t("onboarding.hours.arrivalHint")}
      </p>
      {error && (
        <p role="alert" className={css({ fontSize: "sm", color: "red.default" })}>
          {error}
        </p>
      )}
      <button type="button" onClick={() => edit(applyFirstOpenDay(days))} className={secondaryBtn}>
        {t("onboarding.hours.applyToAll")}
      </button>
      <div className={css({ display: "flex", flexDirection: "column", gap: "3" })}>
        {HOUR_DAYS.map((day, i) => {
          const row = days[i]!;
          const issue = issues.get(i);
          return (
            <div
              key={day}
              className={css({
                display: "grid",
                gridTemplateColumns: { base: "1fr", sm: "8rem 1fr" },
                alignItems: "start",
                gap: "2",
              })}
            >
              <label className={css({ display: "flex", alignItems: "center", gap: "2" })}>
                <input
                  type="checkbox"
                  checked={row.open}
                  onChange={(e) => setDay(i, { open: e.target.checked })}
                />
                <span className={css({ fontSize: "sm", fontWeight: "medium" })}>
                  {t(`schedule.day.${day}`)}
                </span>
              </label>
              {row.open ? (
                <div className={css({ display: "flex", flexDirection: "column", gap: "2" })}>
                  {rangeRow(i, "lunch")}
                  {rangeRow(i, "dinner")}
                  {issue && (
                    <span role="alert" className={css({ fontSize: "xs", color: "red.default" })}>
                      {t(`onboarding.hours.error.${issue}`)}
                    </span>
                  )}
                </div>
              ) : (
                <span className={css({ fontSize: "sm", color: "fg.muted" })}>
                  {t("onboarding.hours.closed")}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
        <button type="button" onClick={() => void save()} disabled={saving} className={primaryBtn}>
          {t("onboarding.save")}
        </button>
        <SavedNote show={saved} label={t("onboarding.saved")} />
      </div>
      <p className={css({ fontSize: "xs", color: "fg.muted" })}>
        {t("onboarding.hours.advanced")}{" "}
        <Link to="../schedule" className={css({ textDecoration: "underline" })}>
          {t("onboarding.step.HOURS")}
        </Link>
      </p>
    </div>
  );
}

/** LISTING_AUTH step — the FR policy text (ADR-0013) + a grant/decline/defer decision (R3–R5). */
export function ListingAuthStep({ venueId, onSaved }: { venueId: string; onSaved: () => void }) {
  const { t } = useTranslation();
  const listing = useQuery({
    queryKey: onboardingKeys.listingAuth(venueId),
    queryFn: () => onboardingApi.getListingAuthorization(venueId),
  });

  async function decide(decision: ListingDecision) {
    await onboardingApi.recordListingDecision(venueId, decision, listing.data?.version ?? 0);
    onSaved();
  }

  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "2xl" })}>
      <p className={css({ fontSize: "sm", color: "fg.muted", whiteSpace: "pre-line" })}>
        {t("onboarding.listing.policy")}
      </p>
      {listing.data?.reConfirmRequired && (
        <output className={css({ display: "block", fontSize: "sm", color: "amber.default" })}>
          {t("onboarding.listing.reconfirm")}
        </output>
      )}
      {listing.data?.decision && (
        <p className={css({ fontSize: "xs", color: "fg.muted" })}>
          {t("onboarding.listing.current", {
            decision: t(`onboarding.decision.${listing.data.decision}`),
          })}
        </p>
      )}
      <div className={css({ display: "flex", gap: "3", flexWrap: "wrap" })}>
        <button type="button" onClick={() => void decide("GRANTED")} className={primaryBtn}>
          {t("onboarding.listing.grant")}
        </button>
        <button
          type="button"
          onClick={() => void decide("DECLINED")}
          className={css({
            px: "4",
            py: "2",
            borderRadius: "md",
            border: "1px solid",
            borderColor: "border.default",
            fontSize: "sm",
            cursor: "pointer",
          })}
        >
          {t("onboarding.listing.decline")}
        </button>
        <button
          type="button"
          onClick={() => void decide("DEFERRED")}
          className={css({
            px: "4",
            py: "2",
            borderRadius: "md",
            border: "1px solid",
            borderColor: "border.default",
            fontSize: "sm",
            cursor: "pointer",
          })}
        >
          {t("onboarding.listing.defer")}
        </button>
      </div>
    </div>
  );
}

/**
 * An OPTIONAL step owned by another feature (hours / photos / staff) — a friendly link to its editor.
 * These are recommended, not required to finish setup, so the copy says so and the CTA names the task
 * ("Définir les horaires") instead of a generic "Ouvrir l'éditeur".
 */
export function LinkStep({
  progress,
  step,
  to,
}: {
  progress: OnboardingProgressResponse;
  step: string;
  to: string;
}) {
  const { t } = useTranslation();
  const done = progress.steps[step as keyof typeof progress.steps] === "COMPLETE";
  return (
    <div className={css({ display: "flex", flexDirection: "column", gap: "3", maxW: "xl" })}>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>{t(`onboarding.link.${step}`)}</p>
      <p className={css({ fontSize: "sm" })}>
        {done ? t("onboarding.link.done") : t("onboarding.link.todo")}
      </p>
      <p className={css({ fontSize: "xs", color: "fg.muted" })}>{t("onboarding.link.optional")}</p>
      <Link
        to={to}
        className={primaryBtn}
        style={{ display: "inline-block", width: "fit-content" }}
      >
        {t(`onboarding.link.cta.${step}`)}
      </Link>
    </div>
  );
}
